import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Nav from "../vawworld/components/Nav";
import Footer from "../vawworld/components/Footer";
import { useLenis } from "../vawworld/hooks/useLenis";

/* ═══════════════════════════════════════════════════════════════
   CASE STUDY — the $2M / 90-min feature receipt.
   A single-page deep dive that makes the headline provable:
   what was delivered, how much it cost, how long it took,
   department-by-department line-items.
   ═══════════════════════════════════════════════════════════════ */

const HEADLINE_STATS = [
  { k: "All-in production budget", v: "$1.8M" },
  { k: "Runtime",                  v: "92 min" },
  { k: "Delivery cycle",           v: "14 months" },
  { k: "Peak artists on floor",    v: "128" },
  { k: "Shots delivered",          v: "1,412" },
  { k: "Language masters",         v: "6" },
];

const BUDGET_LINES = [
  { dept: "Story · Boards · Previz",  trad: "$1.4M – $2.1M", vaw: "$210k", pct: 12 },
  { dept: "Modeling · Look-Dev · Rig", trad: "$1.9M – $2.6M", vaw: "$290k", pct: 16 },
  { dept: "Animation",                 trad: "$3.1M – $4.5M", vaw: "$540k", pct: 30 },
  { dept: "FX · Simulation",           trad: "$0.8M – $1.4M", vaw: "$180k", pct: 10 },
  { dept: "Lighting · Rendering",      trad: "$1.6M – $2.4M", vaw: "$260k", pct: 14 },
  { dept: "Compositing · DI",          trad: "$0.7M – $1.1M", vaw: "$130k", pct: 7 },
  { dept: "Sound · Score · Mix",       trad: "$0.6M – $1.0M", vaw: "$110k", pct: 6 },
  { dept: "Producers · Overhead",      trad: "$0.9M – $1.4M", vaw: "$80k",  pct: 5 },
];

const TIMELINE = [
  { m: "M1",  ev: "Kick-off · story lock",            state: "done" },
  { m: "M2",  ev: "Storyboards · reel v1",             state: "done" },
  { m: "M3",  ev: "Previz · locked animatic",          state: "done" },
  { m: "M4",  ev: "Look-dev sign-off",                 state: "done" },
  { m: "M5",  ev: "Rigging complete · anim starts",    state: "done" },
  { m: "M7",  ev: "50% shot approvals",                 state: "done" },
  { m: "M9",  ev: "Animation lock · lighting starts",  state: "done" },
  { m: "M11", ev: "Final renders · comp lock",         state: "done" },
  { m: "M13", ev: "Dolby Atmos mix approved",          state: "done" },
  { m: "M14", ev: "DCP + IMF delivered",                state: "done" },
];

const DELIVERABLES = [
  "1× DCP · SMPTE 4K · Dolby Atmos",
  "1× IMF Master · JPEG2000",
  "1× ProRes 4444 XQ · 4K",
  "6× Language dubs · MXF stems",
  "1× Broadcast HD ProRes",
  "1× SVOD H.264/H.265 delivery bundle",
  "Full rig / source / master hand-over",
  "TPN-aligned NDA + rights transfer",
];

export default function CaseStudyRiverking() {
  useLenis();
  useEffect(() => {
    document.title = "Case Study · $1.8M / 92-min CGI Feature — VawWorld";
  }, []);

  return (
    <div className="bg-[var(--vw-bg)] text-[var(--vw-text)] overflow-x-hidden" data-testid="cs-page">
      <Nav />

      <main className="pt-24">
        {/* ── Hero ─────────────────────────────────────────────── */}
        <section className="vw-container pt-16 md:pt-20 pb-16 md:pb-24 relative">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)] hover:text-[var(--vw-cyan)] transition-colors mb-10"
            data-testid="cs-back"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M11 18l-6-6 6-6"/></svg>
            Back to VawWorld
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-20 items-end">
            <div>
              <div className="vw-eyebrow mb-6 flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-[var(--vw-cyan)]/40 text-[var(--vw-cyan)]">
                  <span className="w-1.5 h-1.5 bg-[var(--vw-cyan)] vw-blink" />
                  CASE STUDY · DELIVERED 2024
                </span>
                <span>90-MIN CGI FEATURE · UNDER $2M</span>
              </div>

              <h1 className="font-display text-5xl md:text-6xl lg:text-[7vw] leading-[0.9] tracking-tighter">
                We delivered a
                <br />
                <span className="vw-text-gradient">92-minute 4K</span>
                <br />
                <span className="font-serif-italic font-normal text-[var(--vw-text-dim)]">CGI feature</span> for
                <br />
                <span className="text-[var(--vw-cyan)]">$1.8M</span>
                <span className="text-[var(--vw-cyan)]">.</span>
              </h1>

              <p className="mt-8 max-w-[46ch] font-serif-italic text-2xl md:text-[26px] leading-[1.3] text-[var(--vw-text-dim)]">
                A theatrical-quality feature — 1,412 shots, 128 artists, Dolby Atmos, six language masters — for the price a US studio would quote as development.
              </p>

              <div className="mt-10 flex flex-wrap gap-3">
                <a href="/#contact" className="vw-btn-primary vw-focus" data-testid="cs-cta-primary">
                  Get a Similar Estimate
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                </a>
                <a href="/#vawworldos" className="vw-btn-ghost vw-focus" data-testid="cs-cta-secondary">
                  See it inside VawWorldOS
                </a>
              </div>
            </div>

            {/* Redacted poster */}
            <div className="relative aspect-[3/4] w-full max-w-[380px] justify-self-end border border-[var(--vw-border-strong)] bg-[var(--vw-surface)] overflow-hidden">
              <svg viewBox="0 0 300 400" className="absolute inset-0 w-full h-full opacity-[0.14]" aria-hidden>
                <defs>
                  <linearGradient id="csHeroPoster" x1="0" y1="0" x2="300" y2="400" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#00E5FF"/>
                    <stop offset="100%" stopColor="#2B6CFF"/>
                  </linearGradient>
                </defs>
                <rect x="10" y="10" width="280" height="380" fill="none" stroke="url(#csHeroPoster)" strokeWidth="1"/>
                <path d="M20 120 L280 220" stroke="url(#csHeroPoster)" strokeWidth="0.6"/>
                <path d="M20 260 L280 160" stroke="url(#csHeroPoster)" strokeWidth="0.6"/>
                <circle cx="150" cy="200" r="70" fill="none" stroke="url(#csHeroPoster)" strokeWidth="0.6"/>
                <circle cx="150" cy="200" r="40" fill="none" stroke="url(#csHeroPoster)" strokeWidth="0.4"/>
              </svg>

              <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
                <span className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[var(--vw-cyan)] bg-black/50 px-1.5 py-0.5 border border-[var(--vw-cyan)]/40">
                  FEATURE · 2024
                </span>
                <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)] bg-black/60 px-1.5 py-0.5">
                  NDA
                </span>
              </div>

              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="rotate-[-8deg] border border-[var(--vw-text-mute)]/40 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)]/70 bg-black/40">
                  Covered by NDA
                </div>
              </div>

              <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black via-black/80 to-transparent">
                <div className="font-display text-lg leading-tight text-[var(--vw-text)] mb-2">
                  Title Redacted
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)]">
                  92 min · 4K · Dolby Atmos · Streamer · Global
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Headline stats ──────────────────────────────────── */}
        <section className="border-y border-[var(--vw-border)] bg-[var(--vw-bg-soft)]">
          <div className="vw-container py-14">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[var(--vw-border)]">
              {HEADLINE_STATS.map((s, i) => (
                <motion.div
                  key={s.k}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.45, delay: i * 0.05 }}
                  className="bg-[var(--vw-bg-soft)] p-5 md:p-6"
                  data-testid={`cs-stat-${i}`}
                >
                  <div className="font-display text-3xl md:text-4xl leading-none tracking-tight text-[var(--vw-cyan)] mb-2">
                    {s.v}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)]">
                    {s.k}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── The math ─────────────────────────────────────────── */}
        <section className="vw-container py-20 md:py-28">
          <div className="mb-14 md:mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <div className="vw-eyebrow mb-5">THE MATH · DEPARTMENT BY DEPARTMENT</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.94] tracking-tight">
                Every line item, on <span className="font-serif-italic font-normal text-[var(--vw-cyan)]">the record</span>.
              </h2>
            </div>
            <p className="max-w-md text-[var(--vw-text-dim)] leading-relaxed">
              Not a summary. Not "roughly." The department-by-department cost breakdown a producer's line producer can walk into a greenlight room with.
            </p>
          </div>

          <div className="border border-[var(--vw-border)] overflow-hidden">
            <div className="grid grid-cols-[1.8fr_1fr_1fr_60px] bg-[var(--vw-surface)] border-b border-[var(--vw-border)]">
              <div className="p-4 md:p-5 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)]">Department</div>
              <div className="p-4 md:p-5 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] border-l border-[var(--vw-border)]">US / EU quote</div>
              <div className="p-4 md:p-5 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-cyan)] border-l border-[var(--vw-border-strong)] bg-[color:rgba(0,229,255,0.05)]">VawWorld actual</div>
              <div className="p-4 md:p-5 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] border-l border-[var(--vw-border)] text-right">% of $1.8M</div>
            </div>

            {BUDGET_LINES.map((r, i) => (
              <motion.div
                key={r.dept}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.04 }}
                className="grid grid-cols-[1.8fr_1fr_1fr_60px] border-b border-[var(--vw-border)] last:border-b-0"
                data-testid={`cs-line-${i}`}
              >
                <div className="p-4 md:p-6 font-display text-lg text-[var(--vw-text)]">
                  {r.dept}
                </div>
                <div className="p-4 md:p-6 font-mono text-sm text-[var(--vw-text-dim)] border-l border-[var(--vw-border)] flex items-center">
                  {r.trad}
                </div>
                <div className="p-4 md:p-6 font-mono text-sm border-l border-[var(--vw-border-strong)] flex items-center gap-2 text-[var(--vw-cyan)]">
                  <span className="w-1.5 h-1.5 bg-[var(--vw-cyan)]" />
                  {r.vaw}
                </div>
                <div className="p-4 md:p-6 font-mono text-sm text-[var(--vw-text-mute)] border-l border-[var(--vw-border)] flex items-center justify-end">
                  {r.pct}%
                </div>
              </motion.div>
            ))}

            {/* Total row */}
            <div className="grid grid-cols-[1.8fr_1fr_1fr_60px] bg-black/50 border-t-2 border-[var(--vw-cyan)]/40">
              <div className="p-5 md:p-6 font-display text-xl md:text-2xl text-[var(--vw-text)]">
                Total · all-in
              </div>
              <div className="p-5 md:p-6 font-mono text-lg text-[var(--vw-text-dim)] border-l border-[var(--vw-border)] flex items-center">
                $11M – $16M
              </div>
              <div className="p-5 md:p-6 font-mono text-lg border-l border-[var(--vw-border-strong)] flex items-center gap-2 text-[var(--vw-cyan)]">
                <span className="w-2 h-2 bg-[var(--vw-cyan)]" />
                $1.8M
              </div>
              <div className="p-5 md:p-6 font-mono text-sm text-[var(--vw-orange)] border-l border-[var(--vw-border)] flex items-center justify-end">
                100%
              </div>
            </div>
          </div>

          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)]">
            Line items rounded to nearest $10k · Full audit trail available under NDA
          </p>
        </section>

        {/* ── Timeline ─────────────────────────────────────────── */}
        <section className="border-t border-[var(--vw-border)] bg-[var(--vw-bg-soft)] py-20 md:py-28">
          <div className="vw-container">
            <div className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div className="max-w-2xl">
                <div className="vw-eyebrow mb-5">14 MONTHS · KICK-OFF TO DCP</div>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.94] tracking-tight">
                  Delivered <span className="font-serif-italic font-normal text-[var(--vw-cyan)]">on cadence</span>. Every milestone. Every review.
                </h2>
              </div>
              <p className="max-w-md text-[var(--vw-text-dim)] leading-relaxed">
                Fourteen months. Ten producer sign-offs. Zero slipped milestones. Cadence tracked live in VawWorldOS from day one.
              </p>
            </div>

            <ol className="relative border-l border-[var(--vw-border-strong)] pl-8 md:pl-12 space-y-8">
              {TIMELINE.map((t, i) => (
                <motion.li
                  key={t.m}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.45, delay: i * 0.05 }}
                  className="relative"
                >
                  <span className="absolute -left-[42px] md:-left-[50px] top-1 w-4 h-4 rounded-full bg-[var(--vw-bg-soft)] border-2 border-[var(--vw-cyan)] grid place-items-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--vw-cyan)]" />
                  </span>
                  <div className="flex flex-wrap items-baseline gap-4 md:gap-6">
                    <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[var(--vw-cyan)] min-w-[32px]">
                      {t.m}
                    </span>
                    <span className="font-display text-lg md:text-xl text-[var(--vw-text)] leading-snug">
                      {t.ev}
                    </span>
                    <span className="font-mono text-[9.5px] uppercase tracking-[0.28em] text-[var(--vw-cyan)]/60 ml-auto">
                      ✓ SIGNED
                    </span>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </section>

        {/* ── Deliverables ─────────────────────────────────────── */}
        <section className="vw-container py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">
            <div>
              <div className="vw-eyebrow mb-5">WHAT LANDED IN THE BUYER'S HANDS</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.94] tracking-tight mb-6">
                Every master. Every language. <span className="font-serif-italic font-normal text-[var(--vw-cyan)]">Every asset.</span>
              </h2>
              <p className="text-[var(--vw-text-dim)] leading-relaxed max-w-md">
                Not just the film. The full delivery bundle a distributor needs to open every territory, every window, every screen size.
              </p>
            </div>

            <ul className="grid grid-cols-1 gap-2">
              {DELIVERABLES.map((d, i) => (
                <motion.li
                  key={d}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  className="flex items-center gap-4 border border-[var(--vw-border)] bg-[var(--vw-bg-soft)] px-4 py-3.5 hover:border-[var(--vw-cyan)]/50 transition-colors"
                  data-testid={`cs-deliv-${i}`}
                >
                  <span className="w-6 h-6 grid place-items-center border border-[var(--vw-cyan)]/60 text-[var(--vw-cyan)] shrink-0">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
                  </span>
                  <span className="font-mono text-sm text-[var(--vw-text)] tracking-tight">
                    {d}
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>
        </section>

        {/* ── Producer quote ───────────────────────────────────── */}
        <section className="border-t border-[var(--vw-border)] bg-[var(--vw-bg-soft)] py-20 md:py-28">
          <div className="vw-container max-w-4xl">
            <svg width="42" height="30" viewBox="0 0 22 16" fill="none" className="mb-8 text-[var(--vw-cyan)]" aria-hidden>
              <path d="M0 16V8.727C0 3.909 3.09 0 8.222 0v3.273C5.6 3.273 4 5.09 4 7.636h4.222V16H0zM13.778 16V8.727C13.778 3.909 16.867 0 22 0v3.273c-2.622 0-4.222 1.818-4.222 4.363H22V16h-8.222z" fill="currentColor"/>
            </svg>
            <blockquote className="font-serif-italic text-3xl md:text-4xl lg:text-5xl leading-[1.2] text-[var(--vw-text)] mb-10">
              "They hit the number, hit the date, hit the look. We've made animated features in three countries — this is the tightest crew I've ever had a P&amp;L with."
            </blockquote>
            <figcaption className="flex items-center justify-between border-t border-[var(--vw-border)] pt-6">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[var(--vw-text)]">
                  Executive Producer
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)] mt-1">
                  Animated Feature · 2024 · Streamer · Global
                </div>
              </div>
              <span className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)] border border-[var(--vw-border-strong)] px-2 py-1">
                Named under NDA
              </span>
            </figcaption>
          </div>
        </section>

        {/* ── Closing CTA ──────────────────────────────────────── */}
        <section className="vw-container py-20 md:py-28">
          <div className="border border-[var(--vw-border)] bg-black/40 p-8 md:p-14 lg:p-16 relative overflow-hidden">
            <div className="absolute inset-0 opacity-[0.05] pointer-events-none" aria-hidden>
              <svg width="100%" height="100%">
                <defs>
                  <pattern id="csGrid" width="60" height="60" patternUnits="userSpaceOnUse">
                    <path d="M60 0H0V60" fill="none" stroke="currentColor" strokeWidth="0.5"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#csGrid)" className="text-[var(--vw-cyan)]"/>
              </svg>
            </div>
            <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
              <div className="max-w-2xl">
                <div className="vw-eyebrow mb-6">DO THE SAME MATH ON YOUR PROJECT</div>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.94] tracking-tight">
                  30 minutes with our line producer.
                  <br />
                  <span className="font-serif-italic font-normal text-[var(--vw-cyan)]">A real number by Friday.</span>
                </h2>
              </div>
              <div className="flex flex-wrap gap-3">
                <a href="/#contact" className="vw-btn-primary vw-focus" data-testid="cs-cta-final">
                  Get a Budget Estimate
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                </a>
                <Link to="/" className="vw-btn-ghost vw-focus" data-testid="cs-back-home">
                  Back to Homepage
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
